import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import Config from '@Config';
import API_URLS from '@Constants/apiUrls.constants';
import { RequestHeaders } from '@Constants/common.constants';
import { logout, setCredentials } from '@Slices/auth.slice';
import { LoginResponse } from '@Typings/auth.types';

import TokenService from './token.service';

/**
 * Create a base for all the APIs
 * 1. Adds access token to all the API requests if the user is logged in and has a valid access token
 * 2. In case the API errors with 401 i.e. unauthorized due to expired/invalid access token but valid refresh token,
 *    - updates the header with basic auth username/password
 *    - gets a new pair of access & refresh token
 *    - updates the authStake with new tokens and also updates the localStorage
 *    - retry hitting the failed request
 */
const baseQuery = fetchBaseQuery({
  baseUrl: Config.REACT_APP_BASE_API_URL,
  prepareHeaders: (headers) => {
    const accessToken = TokenService.getAccessToken() || '';
    if (accessToken && !TokenService.isTokenExpired(accessToken)) {
      headers.set(RequestHeaders.Authorization, `Bearer ${accessToken}`);
    }
    headers.set(RequestHeaders.Accept, 'application/json');
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    const refreshToken = TokenService.getRefreshToken();

    // if refresh token is not present or refresh token is invalid/expired, logout the user
    if (!refreshToken || TokenService.isTokenExpired(refreshToken)) {
      api.dispatch<{
        payload: undefined;
        type: string;
      }>(logout());
    }
    // try to get a new token
    const refreshResult = await baseQuery(
      {
        url: API_URLS.AUTH.GET_NEW_ACCESS_TOKEN,
        method: 'POST',
        body: {
          refreshToken,
        },
      },
      api,
      extraOptions
    );
    if (refreshResult.data) {
      // retry the initial query
      const { data } = <{ data: LoginResponse }>refreshResult;
      api.dispatch(
        setCredentials({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isLoggedIn: true,
          role: TokenService.getUserRole(),
        })
      );
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch<{
        payload: undefined;
        type: string;
      }>(logout());
    }
  }
  return result;
};

/**
 * Base API for all API endpoints
 * To add tags, use enhanceEndpoints
 * To add endpoints, use injectEndpoints
 */
export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
